import fonts from './fonts'
import colors from './colors'
import {ThemeType} from 'types/Theme'

type HeadingProps = {
  fontSize: string
  fontWeight: number
  lineHeight: string
}

/**
 * Headings
 */
const headingFontProps = (appearance: string): HeadingProps => {
  switch (appearance) {
    case 'title1':
      return {
        fontSize: fonts.size.xxl,
        fontWeight: fonts.weight.xl,
        lineHeight: fonts.lineHeight.xxl
      }
    case 'title2':
      return {
        fontSize: fonts.size.xl,
        fontWeight: fonts.weight.lg,
        lineHeight: fonts.lineHeight.xl
      }
    case 'title3':
      return {
        fontSize: fonts.size.lg,
        fontWeight: fonts.weight.lg,
        lineHeight: fonts.lineHeight.lg
      }
    default:
      return {
        fontSize: fonts.size.lg,
        fontWeight: fonts.weight.lg,
        lineHeight: fonts.lineHeight.lg
      }
  }
}

const makeHeading = (appearance: string) => {
  const {fontSize, fontWeight, lineHeight} = headingFontProps(appearance)
  const styles = {
    fontSize,
    fontWeight,
    lineHeight,
    margin: `0 0 ${fontSize}`,
    fontFamily: fonts.family.default
  }

  return styles
}

const heading = {
  title1: makeHeading('title1'),
  title2: makeHeading('title2'),
  title3: makeHeading('title3')
}

/**
 * Text
 */
const textFontSize = {
  regular: fonts.size.md,
  small: fonts.size.sm,
  action: fonts.size.md,
  tag: fonts.size.xs,
  subtitle1: fonts.size.md,
  subtitle2: fonts.size.sm,
  caption: fonts.size.xs,
  overline: fonts.size.xxs,
  input: fonts.size.md
}

const textFontWeight = {
  regular: fonts.weight.sm,
  small: fonts.weight.sm,
  action: fonts.weight.lg,
  tag: fonts.weight.lg,
  subtitle1: fonts.weight.lg,
  subtitle2: fonts.weight.lg,
  caption: fonts.weight.md,
  overline: fonts.weight.lg,
  input: fonts.weight.md
}

const textLineHeight = {
  regular: fonts.lineHeight.lg,
  small: fonts.lineHeight.md,
  action: fonts.lineHeight.lg,
  tag: fonts.lineHeight.xs,
  subtitle1: fonts.lineHeight.md,
  subtitle2: fonts.lineHeight.sm,
  caption: fonts.lineHeight.xs,
  overline: fonts.lineHeight.xs,
  input: fonts.lineHeight.md
}

const makeText = (type: string) => {
  const fontSize = textFontSize[type]
  const fontWeight = textFontWeight[type]
  const lineHeight = textLineHeight[type]

  const styles = {
    fontSize,
    lineHeight,
    fontWeight,
    fontFamily: fonts.family.default
  }

  return styles
}

const text = {
  subtitle1: makeText('subtitle1'),
  subtitle2: makeText('subtitle2'),
  regular: makeText('regular'),
  action: makeText('action'),
  input: makeText('input'),
  small: makeText('small'),
  tag: makeText('tag'),
  caption: makeText('caption'),
  overline: makeText('overline')
}

/**
 * Links
 */
const linkDefault = {
  color: colors.linkColor,
  cursor: 'pointer',
  textDecoration: 'none',
  outline: 'none',
  ...text.action,
  '&:hover': {
    color: colors.linkHoverColor,
    textDecoration: 'underline'
  },
  '&:active': {
    color: colors.linkActiveColor,
    textDecoration: 'underline'
  },
  '&:focus': {
    color: colors.linkActiveColor,
    textDecoration: 'underline'
  },
  '&:visited': {
    color: colors.linkVisitedColor
  }
}

const link = {
  default: linkDefault
}

const contentDefault = {
  color: colors.piano.p600,
  fontWeight: fonts.weight.sm,
  fontSize: fonts.size.sm
}

const typography: ThemeType['typography'] = {
  heading,
  text,
  link,
  contentDefault
}

export default typography
